/**
 * Komal OS - Punch Point Store
 * Manages punch point list, filters, and CRUD actions against the real API
 */

import { create } from 'zustand';
import * as punchPointService from '../services/punchPointService';

const usePunchPointStore = create((set, get) => ({
  // State
  punchPoints: [],
  selectedPunchPoint: null,
  overduePunchPoints: [],
  total: 0,
  filters: {
    project_id: null,
    supervisor_id: null,
    dept_id: null,
    status_filter: null,
  },
  isLoading: false,
  isSubmitting: false,
  error: null,

  // Actions
  setFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),

  clearError: () => set({ error: null }),

  fetchPunchPoints: async (overrideParams = {}) => {
    set({ isLoading: true, error: null });
    try {
      const params = { ...get().filters, ...overrideParams };
      // Strip null/undefined so we don't send empty query params
      Object.keys(params).forEach((key) => {
        if (params[key] === null || params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      const response = await punchPointService.getPunchPoints(params);
      set({
        punchPoints: response.data.punch_points || [],
        total: response.data.total || 0,
        isLoading: false,
      });
      return response.data.punch_points;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load punch points';
      set({ error: message, isLoading: false });
      return [];
    }
  },

  fetchPunchPoint: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await punchPointService.getPunchPoint(id);
      set({ selectedPunchPoint: response.data.punch_point, isLoading: false });
      return response.data.punch_point;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load punch point';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  fetchOverdue: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await punchPointService.getOverduePunchPoints();
      set({ overduePunchPoints: response.data.overdue_punch_points || [], isLoading: false });
      return response.data.overdue_punch_points;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load overdue punch points';
      set({ error: message, isLoading: false });
      return [];
    }
  },

  createPunchPoint: async (data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await punchPointService.createPunchPoint(data);
      // Refresh the list so the new item shows up
      await get().fetchPunchPoints();
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create punch point';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  completePunchPoint: async (id, completionSummary) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await punchPointService.completePunchPoint(id, completionSummary);
      // Update the item in place if it's in the current list
      set((state) => ({
        punchPoints: state.punchPoints.map((pp) =>
          pp.id === id ? { ...pp, status: 'completed' } : pp
        ),
        isSubmitting: false,
      }));
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to complete punch point';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  verifyPunchPoint: async (id, verificationNotes) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await punchPointService.verifyPunchPoint(id, verificationNotes);
      set((state) => ({
        punchPoints: state.punchPoints.map((pp) =>
          pp.id === id ? { ...pp, status: 'verified' } : pp
        ),
        isSubmitting: false,
      }));
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to verify punch point';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  rejectPunchPoint: async (id, rejectionReason) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await punchPointService.rejectPunchPoint(id, rejectionReason);
      set((state) => ({
        punchPoints: state.punchPoints.map((pp) =>
          pp.id === id ? { ...pp, status: 'open' } : pp
        ),
        isSubmitting: false,
      }));
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reject punch point';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  deletePunchPoint: async (id) => {
    set({ isSubmitting: true, error: null });
    try {
      await punchPointService.deletePunchPoint(id);
      set((state) => ({
        punchPoints: state.punchPoints.filter((pp) => pp.id !== id),
        isSubmitting: false,
      }));
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete entry';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  archivePunchPoint: async (id) => {
    set({ isSubmitting: true, error: null });
    try {
      await punchPointService.archivePunchPoint(id);
      // Whichever list (archived or active) is currently shown, this entry
      // no longer belongs in it - drop it locally rather than refetching.
      set((state) => ({
        punchPoints: state.punchPoints.filter((pp) => pp.id !== id),
        isSubmitting: false,
      }));
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to archive entry';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  unarchivePunchPoint: async (id) => {
    set({ isSubmitting: true, error: null });
    try {
      await punchPointService.unarchivePunchPoint(id);
      set((state) => ({
        punchPoints: state.punchPoints.filter((pp) => pp.id !== id),
        isSubmitting: false,
      }));
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to unarchive entry';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },
}));

export default usePunchPointStore;
